import { observable, action, computed } from 'mobx'
import { type WorkspaceOpportunityInvitationStore } from './WorkspaceOpportunityInvitationStore'
import type {
  Alert,
  AlertErrorHandlerFactory,
  AlertFactory,
} from '@/features/shell'
import { tapAction } from '@/utils/mobx-util'
import { WorkspaceInvitationModalViewModel } from './WorkspaceInvitationModalViewModel'
import { type WorkspaceInvitation } from './WorkspaceInvitation'
import { orderBy } from 'lodash-es'
import { formattedTimeUntil } from '@/utils/date-util'

/**
 * View Model for managing the Modal View Modals and Alerts for Workspace Invitations
 * from Workspace Opportunities.
 */
export class WorkspaceOpportunityInvitationsViewModel {
  /**
   * View Models for modals that are actively being shown.
   */
  @observable
  private accessor _invitationModalVms: WorkspaceInvitationModalViewModel[] = []

  /**
   * View Models for modals that are buffered behind a toast and not yet shown.
   */
  @observable
  private accessor _bufferedVms: WorkspaceInvitationModalViewModel[] = []

  /**
   * A toast that indicates pending invitations to Workspaces.  Associated with the
   * toast is a "batch" of buffered invitations behind it.
   */
  private _invitationToast: Alert | null = null

  constructor(
    private readonly _workspaceOpportunityInvitationStore: WorkspaceOpportunityInvitationStore,
    private readonly _newAlert: AlertFactory,
    private readonly _createAlertErrorHandler: AlertErrorHandlerFactory,
  ) {
    // Buffers each incoming invitation behind the invitation toast.
    _workspaceOpportunityInvitationStore.incomingInvitations$
      .pipe(
        tapAction((invitation) => {
          this._bufferedVms.push(this._createVm(invitation))

          this._invitationToast ??= this._setupInvitationToast()
          this._invitationToast.show()
        }),
      )
      .subscribe()
  }

  /**
   * The invitation modal view models to display.
   */
  @computed
  get invitationModalVms() {
    return this._invitationModalVms
  }

  /**
   * Shows the buffered invitations and dismisses the toast.
   */
  @action.bound
  private _showBufferedInvitations() {
    this._invitationModalVms = this._invitationModalVms.concat(
      sortedByPriority(this._bufferedVms),
    )

    this._bufferedVms = []

    this._invitationModalVms.forEach((vm) => vm.modal.show())
    this._invitationToast?.dismiss()
    this._invitationToast = null
  }

  /**
   * Configures the invitation toast with its interaction handler
   * and reactive title and description
   */
  private _setupInvitationToast() {
    return this._newAlert()
      .withState('success')
      .saying(this._toastTitle, this._toastDescription)
      .onInteract(this._showBufferedInvitations)
      .withActions({
        title: 'Review',
        handler: this._showBufferedInvitations,
      })
      .cancelAutoDismiss()
  }

  /**
   * Reactive getter for the toast title
   */
  private readonly _toastTitle = () => {
    return this._bufferedVms.length > 1
      ? `You're invited to ${this._bufferedVms.length} workspaces!`
      : `You're invited to a workspace!`
  }

  /**
   * Reactive getter for the toast description
   */
  private readonly _toastDescription = () => {
    if (this._bufferedVms.length === 0) {
      return ''
    }

    if (this._bufferedVms.length > 1) {
      const highestPriorityVm = orderBy(
        this._bufferedVms,
        (vm) => vm.expirationMillis,
      )[0]

      return `Auto-rejecting in ${formattedTimeUntil(
        highestPriorityVm.timeUntilExpiration,
      )}`
    }

    return `Auto-rejecting in ${formattedTimeUntil(
      this._bufferedVms[0].timeUntilExpiration,
    )}`
  }

  private _createVm(invitation: WorkspaceInvitation) {
    return new WorkspaceInvitationModalViewModel(
      this._workspaceOpportunityInvitationStore,
      invitation,
      this._createAlertErrorHandler,
    )
  }
}

/**
 * Sorts a list of view models by their expiration millis in descending order.
 * Descending order is used so that the offer that will expire soonest is at the
 * end, thus allowing its modal to be drawn over all the others.
 */
function sortedByPriority(vms: WorkspaceInvitationModalViewModel[]) {
  return orderBy(vms, (vm) => vm.expirationMillis, 'desc')
}
